import React from 'react';
import './RouteInfo.scss';
import { BundleRoute, BundleRouteStep } from '../../types/bundleTypes';
import {
  formatEta,
  formatTimePerStop,
  formatWindow,
  getRouteColor,
  getRouteFontColor,
} from './RouteUtils';
import { classNames } from '../../utils/classUtils';

interface Props {
  route: BundleRoute;
  expanded: boolean;
  selected: boolean;
  onSelectRoute: (id: string) => void;
  onDeselectRoute: (id: string) => void;
  onExpand?: (route: BundleRoute, expanded: boolean) => void;
}

export default function RouteInfo({
  route,
  expanded,
  selected,
  onSelectRoute,
  onDeselectRoute,
  onExpand,
}: Props) {
  const backgroundColor = getRouteColor(route.id);
  const color = getRouteFontColor(route.id);
  const orderCount = new Set(route.steps.map(step => step.orderId)).size;
  const windowCount = new Set(
    route.steps.map(step => `${step.windowStart}-${step.windowEnd}`)
  ).size;

  const toggleExpanded = () => {
    if (onExpand) {
      onExpand(route, !expanded);
    }
  };

  const isEarly = (route: BundleRoute, step: BundleRouteStep): boolean => {
    const windowStart = Date.parse(`${route.date}T${step.windowStart}`) / 1000;
    const eta = Date.parse(`${route.date}T${step.eta}`) / 1000;
    return eta - windowStart < -60;
  };

  const isLate = (route: BundleRoute, step: BundleRouteStep): boolean => {
    const windowEnd = Date.parse(`${route.date}T${step.windowEnd}`) / 1000;
    const eta = Date.parse(`${route.date}T${step.eta}`) / 1000;
    return eta - windowEnd > 60;
  };

  const deliveryClass = (route: BundleRoute, step: BundleRouteStep): string => {
    if (!step.deliveredAt) return undefined;
    const deliveredAt = Date.parse(`${route.date}T${step.deliveredAt}`) / 1000;
    const eta = Date.parse(`${route.date}T${step.eta}`) / 1000;

    const diffMinutes = (deliveredAt - eta) / 60;
    if (diffMinutes < -60) {
      return 'route-info-instructions__delivered-earliest';
    } else if (diffMinutes < -30) {
      return 'route-info-instructions__delivered-earlier';
    } else if (diffMinutes < -10) {
      return 'route-info-instructions__delivered-early';
    } else if (diffMinutes > 10) {
      return 'route-info-instructions__delivered-late';
    } else if (diffMinutes > 30) {
      return 'route-info-instructions__delivered-later';
    } else if (diffMinutes > 60) {
      return 'route-info-instructions__delivered-latest';
    }
    return undefined;
  };

  const formatDeliveredAt = (step: BundleRouteStep): string => {
    return step.deliveredAt ? step.deliveredAt.substring(0, 5) : '';
  };

  return (
    <div key={`route-info-${route.id}`} className="route-info">
      <div className="route-info-header" onClick={toggleExpanded}>
        <div style={{ backgroundColor, color }}>
          <label
            onClick={e => e.stopPropagation()}
            htmlFor={`routeTitle${route.id}`}
          >
            <input
              className="route-info-input-checkbox"
              id={`routeTitle${route.id}`}
              type="checkbox"
              checked={selected}
              onChange={event =>
                event.target.checked
                  ? onSelectRoute(route.id)
                  : onDeselectRoute(route.id)
              }
            />
            <span className="route-info-value route-info-id">
              ID: {route.id}
            </span>
          </label>
        </div>
        <div>
          <span className="route-info-checkbox">
            Orders: <span className="route-info-value">{orderCount}</span>
          </span>
        </div>
        <div>
          Windows: <span className="route-info-value">{windowCount}</span>
        </div>
        <div>
          Stop:{' '}
          <span className="route-info-value">{formatTimePerStop(route)}</span>
        </div>
      </div>
      <div className="route-info-instructions">
        <div className="route-info-instruction-container">
          <div className="route-info-instructions--lines">
            {expanded && (
              <>
                <div className="route-info-instructions__line route-info-instructions__header">
                  <div className="route-info-instructions__step">Step</div>
                  <div className="route-info-instructions__orderid">Order</div>
                  <div className="route-info-instructions__address">
                    Address
                  </div>
                  <div className="route-info-instructions__times">Window</div>
                  <div className="route-info-instructions__eta">ETA</div>
                  <div className="route-info-instructions__delivered">
                    Delivered
                  </div>
                </div>
                {route.steps.map(
                  (step, idx) =>
                    idx > 0 && (
                      <div
                        key={`${step.orderId}-${idx}`}
                        className="route-info-instructions__line"
                      >
                        <div className="route-info-instructions__type">
                          <span>{idx}</span>
                        </div>
                        <div className="route-info-instructions__orderid">
                          {step.orderId}
                        </div>
                        <div className="route-info-instructions__address">
                          <span>{step.address}</span>
                        </div>
                        <div className="route-info-instructions__times">
                          <span>{formatWindow(step)}</span>
                        </div>
                        <div
                          className={classNames(
                            {
                              'route-info-instructions__eta-early': isEarly(
                                route,
                                step
                              ),
                              'route-info-instructions__eta-late': isLate(
                                route,
                                step
                              ),
                            },
                            'route-info-instructions__eta'
                          )}
                        >
                          <span>{formatEta(step)}</span>
                        </div>
                        <div
                          className={[
                            'route-info-instructions__delivered',
                            deliveryClass(route, step),
                          ].join(' ')}
                        >
                          <span>{formatDeliveredAt(step)}</span>
                        </div>
                      </div>
                    )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
